import { type FC } from "react";

import { type MylensesSubmissionDetailsSource } from "@snapchat/blizzard-schema";
import { TextArea } from "@snapchat/snap-design-system";
import { useIntl } from "react-intl";

import { useFeatureGates } from "../../../../cof-feature-gates/hooks";
import { CofConfigId, SurfaceType } from "../../../../gql-generated/types";
import { LENS_DESCRIPTION_MAX_CHARS } from "../../../../lens/components/constants";
import { LensCtaUI } from "../../../../lens-cta/components/LensCtaUI";
import { type LensPreviewStateHook } from "../../../../lens-preview/hooks";
import { defaultPreviewMediaItem } from "../../../../shared/state";

import { LensDetailsRow } from "./LensDetailsRow";
import { messages } from "./messages.generated";
import { type EditDetailsResult } from "./useEditDetails";

interface LensEditDetailsBaseProps extends Partial<EditDetailsResult> {
    lensPreviewProps: LensPreviewStateHook;
    iconUrl: string;
    createChangeDetailsEvent: (source: MylensesSubmissionDetailsSource) => () => void;
    lensOrSubmissionId: string;
    lensForUpdate: { description: string } | undefined;
    surfaceType: SurfaceType;
    useSplitPanelLayout?: boolean;
}

export const LensEditDetailsBase: FC<LensEditDetailsBaseProps> = ({
    lensPreviewProps,
    iconUrl,
    createChangeDetailsEvent,
    lensOrSubmissionId,
    lensForUpdate,
    surfaceType,
    lensDescription,
    setLensDescription,
    useSplitPanelLayout = true,
}) => {
    const intl = useIntl();
    const { configs } = useFeatureGates();
    const isLensDescriptionEnabled = configs[CofConfigId.MyLensesLensDescription].enabled;
    const isLensCtaEnabled = configs[CofConfigId.MyLensesCta].enabled;

    return (
        <>
            {surfaceType === SurfaceType.SurfaceTypeSpectacles && isLensDescriptionEnabled && (
                <TextArea
                    label={intl.formatMessage(messages.lensDescriptionLabel)}
                    placeholder={intl.formatMessage(messages.lensDescriptionPlaceholder)}
                    value={lensDescription ?? lensForUpdate?.description ?? ""}
                    onChange={setLensDescription}
                    maxLength={LENS_DESCRIPTION_MAX_CHARS}
                    autoSize={{ minRows: 3 }}
                />
            )}
            {surfaceType === SurfaceType.SurfaceTypeSnapchat && isLensCtaEnabled && (
                <>
                    {useSplitPanelLayout ? (
                        <LensDetailsRow
                            dataTestId="lens-cta-row"
                            title={intl.formatMessage(messages.lensCtaSectionTitle)}
                        >
                            <LensCtaUI
                                lensPreview={lensPreviewProps.previewMediaItems[0] ?? defaultPreviewMediaItem}
                                lensIconURL={iconUrl}
                                label={intl.formatMessage(messages.lensCtaLabel)}
                                tooltipContent={intl.formatMessage(messages.lensCtaTooltip)}
                                createChangeDetailsEvent={createChangeDetailsEvent}
                                lensOrSubmissionId={lensOrSubmissionId}
                            />
                        </LensDetailsRow>
                    ) : (
                        <div data-testid="lens-cta-row">
                            <LensCtaUI
                                lensPreview={lensPreviewProps.previewMediaItems[0] ?? defaultPreviewMediaItem}
                                lensIconURL={iconUrl}
                                label={intl.formatMessage(messages.lensCtaLabel)}
                                tooltipContent={intl.formatMessage(messages.lensCtaTooltip)}
                                createChangeDetailsEvent={createChangeDetailsEvent}
                                lensOrSubmissionId={lensOrSubmissionId}
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
};
